import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import SharedLayout from './components/SharedLayout';
import Login from './components/Login';
import Register from './components/Register';
import Dashboard from './components/Dashboard';
import MVP from './components/MVP';
import NewProfile from './components/NewProfile';
import Mindset from './components/Mindset';
import MindsetPage from './components/MindsetPage';
import DailySuccessJournal from './components/DailySuccessJournal';
import RolesAndGoals from './components/RolesAndGoals';
import DailySuccessCheckList from './components/DailySuccessCheckList';
import WeeklySuccessJournal from './components/WeeklySuccessJournal';
import DailyPlanner from './components/DailyPlanner';
import TwoWeekPlanner from './components/TwoWeekPlanner';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import { UserProvider, useUser } from './context/UserContext';
import { TaskProvider } from './context/TaskContext';
import { DailyCheckInProvider } from './context/DailyCheckInContext';
import Profile from './components/Profile';
import MindsetScript from './components/MindsetScript';
import PerformanceJournal from './components/PerformanceJournal';
import Journal from './components/Journal';
import Plan from './components/Plan';
import Onboarding from './components/Onboarding';

import Focus from './components/Focus';
import Breathwork from './components/Breathwork';
import Relaxation from './components/Relaxation';
import ConcentrationGrid from './components/ConcentrationGrid';
import RoutineBuilder from './components/RoutineBuilder';
import MentalImagery from './components/MentalImagery';
import Mixpanel from './utils/mixpanel';
import TermsAndConditions from './components/TermsAndConditions';
import PrivacyPolicy from './components/PrivacyPolicy';
import AdminDashboard from './components/AdminDashboard';
import PageTracker from './utils/PageTracker';
import ScrollToTop from './components/ScrollToTop';
import InactivityTracker from './components/InactivityTracker';
import SessionManager from './components/SessionManager';
import Home from './components/Home';
import logger from './utils/logger';
import AudioVisualization from './components/AudioVisualization';
import CoachDashboard from './components/CoachDashboard';

// Inline Google Auth Callback Component
const GoogleAuthCallback = () => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get('token');
    const userId = queryParams.get('userId');
    const error = queryParams.get('error');

    if (error) {
      setError(error);
      setLoading(false);
      return;
    }

    if (token && userId) {
      // Set auth data to localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('userId', userId);
      
      // Navigate to dashboard
      navigate('/dashboard');
    } else {
      setError('No authentication data received');
      setLoading(false);
    }
  }, [location, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded shadow-md text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Completing authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="bg-white p-8 rounded shadow-md text-center">
        <div className="text-red-500 mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 className="text-xl font-bold mt-2">Authentication Error</h2>
        </div>
        <p className="text-gray-600 mb-4">{error || 'Failed to authenticate with Google. Please try again.'}</p>
        <button
          onClick={() => navigate('/login')}
          className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded"
        >
          Go to Login
        </button>
      </div>
    </div>
  );
};

const PrivateRoute = ({ children }) => {
  const { user, isAuthenticated, loading, setIsAuthenticated } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [localChecked, setLocalChecked] = useState(false);
  
  // Add logging to help debug authentication issues
  logger.debug('PrivateRoute - Auth State:', { user: !!user, isAuthenticated, loading, path: location.pathname });
  
  // Check localStorage directly as a fallback
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    
    logger.debug('PrivateRoute - Direct localStorage check:', { 
      hasToken: !!token, 
      hasUserId: !!userId,
      isAuthenticated 
    });
    
    // If we have a token and userId in localStorage but isAuthenticated is false,
    // force the authentication state to true
    if (token && userId && !isAuthenticated && !loading) {
      logger.debug('PrivateRoute - Found valid credentials in localStorage, forcing authentication state');
      setIsAuthenticated(true);
    }
    
    setLocalChecked(true);
  }, [isAuthenticated, loading, setIsAuthenticated]);

  // Show loading indicator while authentication state is being determined
  if (loading || !localChecked) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <p className="ml-3 text-gray-600">Verifying authentication...</p>
      </div>
    );
  }

  // Check if we have a token in localStorage as a fallback
  const hasLocalToken = !!localStorage.getItem('token');
  const hasLocalUserId = !!localStorage.getItem('userId');
  
  // Only render the protected content if authenticated OR we have valid local credentials
  const isAuthorized = isAuthenticated || (hasLocalToken && hasLocalUserId);
  
  logger.debug('PrivateRoute - Final authorization decision:', { 
    isAuthenticated, 
    hasLocalToken, 
    hasLocalUserId,
    isAuthorized
  });
  
  return isAuthorized ? (
    <SharedLayout>
      {children}
    </SharedLayout>
  ) : (
    <Navigate to="/login" replace state={{ from: location.pathname }} />
  );
};

function App() {
  const token = localStorage.getItem('token');
  
  useEffect(() => {
    Mixpanel.track('App Loaded');
  }, []);

  return (
    <UserProvider>
      <TaskProvider>
        <DailyCheckInProvider>
          <Router>
            <PageTracker />
            <ScrollToTop />
            <SessionManager />
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Home />} />
              <Route path="/home" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/new-profile" element={<NewProfile />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
              <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/onboarding" element={<Onboarding />} />
              
              {/* Protected routes */}
              <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
              <Route path="/mvp" element={<PrivateRoute><MVP /></PrivateRoute>} />
              <Route path="/mindset" element={<PrivateRoute><MindsetPage /></PrivateRoute>} />
              <Route path="/mindset-script" element={<PrivateRoute><MindsetScript /></PrivateRoute>} />
              <Route path="/journal" element={<PrivateRoute><Journal /></PrivateRoute>} />
              <Route path="/daily-journal" element={<PrivateRoute><DailySuccessJournal /></PrivateRoute>} />
              <Route path="/weekly-journal" element={<PrivateRoute><WeeklySuccessJournal /></PrivateRoute>} />
              <Route path="/performance-journal" element={<PrivateRoute><PerformanceJournal /></PrivateRoute>} />
              <Route path="/roles-and-goals" element={<PrivateRoute><RolesAndGoals /></PrivateRoute>} />
              <Route path="/daily-success-checklist" element={<PrivateRoute><DailySuccessCheckList /></PrivateRoute>} />
              <Route path="/daily-planner" element={<PrivateRoute><DailyPlanner /></PrivateRoute>} />
              <Route path="/two-week-planner" element={<PrivateRoute><TwoWeekPlanner /></PrivateRoute>} />
              <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
              <Route path="/plan" element={<PrivateRoute><Plan /></PrivateRoute>} />
              <Route path="/focus" element={<PrivateRoute><Focus /></PrivateRoute>} />
              <Route path="/breathwork" element={<PrivateRoute><Breathwork /></PrivateRoute>} />
              <Route path="/relaxation" element={<PrivateRoute><Relaxation /></PrivateRoute>} />
              <Route path="/concentration-grid" element={<PrivateRoute><ConcentrationGrid /></PrivateRoute>} />
              <Route path="/routine-builder" element={<PrivateRoute><RoutineBuilder /></PrivateRoute>} />
              <Route path="/mental-imagery" element={<PrivateRoute><MentalImagery /></PrivateRoute>} />
              <Route path="/admin" element={<PrivateRoute><AdminDashboard /></PrivateRoute>} />
              <Route path="/audio-visualization" element={<PrivateRoute><AudioVisualization /></PrivateRoute>} />
              <Route path="/coach-dashboard" element={<PrivateRoute><CoachDashboard /></PrivateRoute>} />
            </Routes>
          </Router>
        </DailyCheckInProvider>
      </TaskProvider>
    </UserProvider>
  );
}

export default App;